import React, { useEffect, useMemo, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { Box, TextField } from '@material-ui/core';
import {
  fetchBrandReportingUnits,
  fetchReportingUnitGroupRestaurants,
  fetchReportingUnitGroups,
} from '../../redux/actions/reportingUnitsActions';
import {
  getFranchiseGroups,
  getReportingUnitById,
  getReportingUnitsByBrand,
} from '../../redux/selectors/reportingUnitsSelectors';

import RBIAutocomplete from '../RBIAutocomplete/RBIAutocomplete';
import { TreeSelector } from '../TreeSelector';
import { useTranslation } from 'react-i18next';

/**
 *
 * @param value
 * @param tabIndex
 * @param assignedRestaurants
 * @param onFranchiseGroupSelect
 * @param onChange
 * @constructor
 * @type {React.Component}
 */
export function CorporateRestaurantSelector({
  value,
  tabIndex = 0,
  assignedRestaurants,
  onFranchiseGroupSelect,
  onChange = (values: any[]) => {
    // No-op: Default implementation
  },
}: any) {
  const dispatch: any = useDispatch();

  const [selectedReportingUnit, setSelectedReportingUnit] = useState<any>({});
  const [selectedFranchiseGroup, setSelectedFranchiseGroup] = useState<any>({});
  const [filterValue, setFilterValue] = useState('');
  const [filterDelayedValue, setFilterDelayedValue] = useState('');
  const selectedBrand = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedBrand,
  );

  const [filterTimer, setFilterTimer] = useState<any>(null);
  const reportingUnits = useSelector(getReportingUnitsByBrand(selectedBrand));
  const reportingUnit = useSelector(
    getReportingUnitById(selectedBrand, selectedReportingUnit?.id),
  );
  const franchiseGroups = useSelector(getFranchiseGroups(selectedBrand));

  const franchiseGroupsOptions = useMemo(() => {
    let options = [];
    if (reportingUnit?.groups) {
      options = reportingUnit.groups;
    }
    return options;
  }, [reportingUnit?.groups]);

  const restaurantsNodes = useMemo(() => {
    let nodes = null;
    const franchiseGroup = franchiseGroups[selectedFranchiseGroup.id];
    if (franchiseGroup?.restaurants) {
      nodes = franchiseGroup?.restaurants.map(restaurant => ({
        text: `# ${restaurant.number} | ${restaurant.name}`,
        path: `/${selectedBrand}/${reportingUnit.id}/${franchiseGroup.id}/${restaurant.id}`,
        type: 'Restaurant',
        hasChildren: false,
        metas: {
          groupId: selectedReportingUnit.id,
          reportUnitId: reportingUnit.id,
          reportUnitName: reportingUnit.name,
          restaurantId: restaurant.id,
        },
      }));
    }
    return nodes;
  }, [selectedFranchiseGroup, franchiseGroups]);

  const restaurantNodesPagination = useMemo(() => {
    let pagination = {};
    const franchiseGroup = franchiseGroups[selectedFranchiseGroup.id];

    if (franchiseGroup?.restaurants) {
      pagination = {
        ...franchiseGroup.restaurantsPagination,
        steps: 1900,
      };
    }

    return pagination;
  }, [selectedFranchiseGroup, franchiseGroups]);

  const hasReportingUnitSelected = useMemo(
    () => !!selectedReportingUnit?.id,
    [selectedReportingUnit],
  );
  const hasFranchiseGroupSelected = useMemo(
    () => !!selectedFranchiseGroup?.id,
    [selectedFranchiseGroup],
  );
  const isLoadingRestaurants = useMemo(
    () => !restaurantsNodes,
    [restaurantsNodes],
  );

  useEffect(() => {
    if (dispatch && !reportingUnits)
      dispatch(fetchBrandReportingUnits(selectedBrand));
  }, [selectedBrand, dispatch]);

  useEffect(() => {
    clearTimeout(filterTimer);
    setFilterTimer(
      setTimeout(() => {
        setFilterDelayedValue(filterValue);
      }, 500),
    );
  }, [filterValue]);

  useEffect(() => {
    const franchiseGroup = franchiseGroups[selectedFranchiseGroup.id];
    if (franchiseGroup) {
      const data = {
        restaurants: franchiseGroup?.restaurants,
        groupId: selectedReportingUnit?.id,
        reportUnitId: reportingUnit?.id,
        reportUnitName: reportingUnit?.name,
        selectedBrand,
        franchiseGroupId: franchiseGroup?.id,
      };
      onFranchiseGroupSelect({ ...data });
    }
  }, [selectedFranchiseGroup, franchiseGroups]);

  const handleReportingUnitChange = (name: string, selection: any) => {
    if (selection && !selection.groups) {
      dispatch(fetchReportingUnitGroups(selectedBrand, selection.id));
    }
    setSelectedReportingUnit(selection || {});
    setSelectedFranchiseGroup({});
  };
  const handleFranchiseGroupChange = (name: string, selection: any) => {
    if (selection && !selection.restaurants) {
      dispatch(
        fetchReportingUnitGroupRestaurants({
          brand: selectedBrand,
          reportUnitId: selectedReportingUnit.id,
          groupId: selection.id,
          params: { limit: 2000 },
        }),
      );
    }
    setSelectedFranchiseGroup(selection || {});
  };
  const handleFilterChange = ({ target }) => {
    setFilterValue(target.value);
  };
  const handleNodeSelectionChange = (values: any[]) => {
    onChange(values);
  };
  const handleFetchNextRestaurants = async (node: any) => {
    const { metas, pagination } = node || {};
    const { groupId, reportUnitId } = metas || {
      groupId: selectedFranchiseGroup.id,
      reportUnitId: selectedReportingUnit.id,
    };
    const {
      total,
      loadedCount,
      pageSize: limit,
      lastPage,
    } = pagination || restaurantNodesPagination;
    if (total > loadedCount) {
      await dispatch(
        fetchReportingUnitGroupRestaurants({
          brand: selectedBrand,
          reportUnitId,
          groupId: groupId,
          params: { limit, page: lastPage + 1 },
        }),
      );
    }
  };

  const { t } = useTranslation();

  const handleDisableCheckOnFiltering = (node: any) => {
    return (
      !tabIndex && assignedRestaurants.ids.includes(node.metas.restaurantId)
    );
  };
  return (
    <Box>
      <Box
        style={{
          padding: '5px 10px',
        }}
      >
        <RBIAutocomplete
          id="lsReportingUnits"
          label={t('productGroups.assignment.filterByReportingUnit')}
          value={selectedReportingUnit}
          options={reportingUnits || []}
          width="100%"
          style={{
            backgroundColor: 'white',
          }}
          freeSolo={false}
          onChangeInput={handleReportingUnitChange}
          getOptionSelected={(option, value) => option.id === value.id}
        />
        <RBIAutocomplete
          id="lsFranchiseGroups"
          label={t('productGroups.assignment.filterByFranchiseGroup')}
          value={selectedFranchiseGroup}
          options={franchiseGroupsOptions}
          width="100%"
          style={{
            backgroundColor: 'white',
          }}
          freeSolo={false}
          disabled={!hasReportingUnitSelected}
          onChangeInput={handleFranchiseGroupChange}
          getOptionSelected={(option, value) => option.id === value.id}
        />
        {hasFranchiseGroupSelected && (
          <TextField
            size="small"
            variant="outlined"
            label={t('productGroups.assignment.filterByRestaurant')}
            fullWidth
            value={filterValue}
            name="restaurantFilter"
            onChange={handleFilterChange}
            autoFocus={false}
            disabled={isLoadingRestaurants}
            style={{
              marginTop: '8px',
            }}
          />
        )}
      </Box>
      <Box flexGrow={1}>
        {hasFranchiseGroupSelected && (
          <TreeSelector
            showStoreIcon={true}
            watchForChange={[
              selectedReportingUnit?.name,
              selectedFranchiseGroup?.name,
            ]}
            nodes={restaurantsNodes}
            value={value}
            pagination={restaurantNodesPagination}
            showLoadingOnRoot={true}
            showNoDataOnRoot={true}
            filterText={filterDelayedValue}
            onChange={handleNodeSelectionChange}
            checkDisabled={handleDisableCheckOnFiltering}
            onNextNodesPage={handleFetchNextRestaurants}
          />
        )}
      </Box>
    </Box>
  );
}
